import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client/react";
import { toast } from "sonner";

import { ConfirmDialog } from "@lytra/frontend-common/components/v2/ui/dialog";
import { TICKET_NOTE_DELETE } from "./noteQueries";

interface DeleteNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The note or reply to delete. */
  messageId: string;
  /** Replies stay readable under a "Notiz gelöscht" card; the description says so. */
  hasReplies: boolean;
  /** Called once the note is deleted and the dialog closed (the host moves focus). */
  onDeleted?: () => void;
}

/**
 * The destructive confirmation before an author deletes a note (D-11). A soft delete: the
 * server keeps replies and withdraws open mentions. "Abbrechen" has the initial focus, so
 * Enter right after opening destroys nothing. While the delete runs the confirm button is
 * loading and neither button reacts (ConfirmDialog's pending state); the dialog closes on
 * success, and on failure too after a toast, so the note stays as it was.
 */
export function DeleteNoteDialog({
  open,
  onOpenChange,
  messageId,
  hasReplies,
  onDeleted,
}: DeleteNoteDialogProps) {
  const { t } = useTranslation();
  const [deleteNote] = useMutation(TICKET_NOTE_DELETE, {
    errorPolicy: "none",
    refetchQueries: ["Ticket"],
    awaitRefetchQueries: true,
  });
  // A second confirm while the first is in flight sends nothing (the repeat is harmless on
  // the server, but the toast and the focus move must happen once).
  const runningRef = useRef(false);
  const deletedRef = useRef(false);
  const onDeletedRef = useRef(onDeleted);
  useEffect(() => {
    onDeletedRef.current = onDeleted;
  });

  // Once per successful delete: when the dialog closes, or when the whole thread goes away
  // first because the refetch removed a note that had no replies.
  const finishDeleted = () => {
    if (!deletedRef.current) return;
    deletedRef.current = false;
    onDeletedRef.current?.();
  };
  useEffect(() => finishDeleted, []);

  const confirm = async (): Promise<void> => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const result = await deleteNote({ variables: { messageId } });
      if (result.data?.noteDelete) {
        deletedRef.current = true;
      } else {
        // Not the viewer's note any more (or gone): the refetch already showed the truth.
        toast(t("tickets.notes.errors.gone"));
      }
    } catch {
      toast.error(t("tickets.notes.errors.actionFailed"));
    } finally {
      runningRef.current = false;
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) finishDeleted();
      }}
      variant="destructive"
      title={t("tickets.notes.deleteDialog.title")}
      description={t(
        hasReplies
          ? "tickets.notes.deleteDialog.withReplies"
          : "tickets.notes.deleteDialog.withoutReplies",
      )}
      cancelLabel={t("tickets.notes.cancel")}
      confirmLabel={t("tickets.notes.deleteDialog.confirm")}
      onConfirm={confirm}
    />
  );
}
